import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { type TaskDetail } from './use-task-detail';
import { invalidateAgentTaskQueries } from './agent-task-invalidation';

/**
 * The user's two WRITES on a task — restate it, and note on it.
 *
 * Both go to the native-data bridge through a thin Next proxy (`/api/tasks/:id/{update,note}`), so web
 * and both phones share one validation and one open-task rule. What lives HERE is only the client half:
 * the in-flight flag, the failure reason, and the cache reconcile.
 *
 * **Reconcile from the server's echo, never from what was typed.** The bridge trims, bounds and may
 * refuse; writing the typed value into the cache optimistically and then never correcting it is how a
 * field comes to show something the server did not store. The detail is re-read instead, which is one
 * request and is what the panel is looking at anyway.
 *
 * **A 409 is not an error message, it is a state change.** It means the task settled while the form was
 * open — so the caller shows the "this has finished" line and the panel's own re-read drops the edit
 * affordance, rather than inviting a retry that cannot succeed.
 */
export type TaskWriteFailure = 'settled' | 'failed';

export interface TaskEditsFlow {
  /** A write is in flight. Disables the form rather than letting a second one race it. */
  busy: boolean;
  /** Why the last write did not land, or null. Cleared when the next one starts. */
  failure: TaskWriteFailure | null;
  /** Restate the task. Returns true when it landed. */
  save: (fields: { title?: string; brief?: string }) => Promise<boolean>;
  /** Note on the task. Returns true when it landed. */
  addNote: (note: string) => Promise<boolean>;
  /** Forget the last failure — for a form that reopens. */
  clearFailure: () => void;
}

export function useTaskEdits(taskId: string): TaskEditsFlow {
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<TaskWriteFailure | null>(null);

  const post = useCallback(
    async (path: 'update' | 'note', body: Record<string, unknown>): Promise<boolean> => {
      setBusy(true);
      setFailure(null);
      try {
        const res = await apiFetch(`${API_BASE}/api/tasks/${encodeURIComponent(taskId)}/${path}`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        if (!res.ok) {
          setFailure(res.status === 409 ? 'settled' : 'failed');
          return false;
        }
        // The server's own copy is the truth — it trimmed, it bounded, and on a note it stamped the date.
        // Every cache holding this task re-reads from the one shared set; the goal ledger is the one
        // addition, because it renders this task's change log and is not a task-keyed entry.
        invalidateAgentTaskQueries(taskId);
        await queryClient.invalidateQueries({ queryKey: ['goal-ledger'] });
        return true;
      } catch {
        setFailure('failed');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [queryClient, taskId],
  );

  return {
    busy,
    failure,
    save: useCallback((fields) => post('update', fields), [post]),
    addNote: useCallback((note) => post('note', { note }), [post]),
    clearFailure: useCallback(() => setFailure(null), []),
  };
}

/** The notes on a task, defaulted — a payload from before notes existed carries no key, and a task is
 *  not "no notes" because a field is missing from an old response. */
export function taskNotes(task: Pick<TaskDetail, 'notes'>): NonNullable<TaskDetail['notes']> {
  return task.notes ?? [];
}
